import React, {useState, useEffect} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {formatDateTimeShort, GlobalState} from "../../../global/GlobalState";
import {Link, useParams} from "react-router-dom";
import {
	Alert,
	Autocomplete,
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle, FormControl, InputLabel, MenuItem,
	Paper, Select,
	Stack, TextField,
	Typography
} from "@mui/material";
import {
	productCategoryColumn,
	productOrderTypeColumn,
	productStatusColumn,
	textColumn,
	tooltipColumn
} from "../../../components/common/grid/Columns";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {COPY_PRODUCTS, GET_EXPORTER_OPTIONS, GET_EXPORT_TEMPLATE, PARTNER_HIERARCHY_INDEX, PRODUCTS_OVERVIEW, SUPPLIER_INDEX, GET_UPLOAD_URL} from "../Queries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {GridFilterItem} from "@mui/x-data-grid/models/gridFilterItem";
import {linkProductDetailsFor, openProductDetailsForNew} from "./ProductsDetails";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {useQuery} from "@apollo/client";
import {translateFunction} from "../../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {PartnerHierarchy} from "../models/PartnerHierarchy";
import {GridRowId} from "@mui/x-data-grid/models/gridRows";
import {targetStageSafetyCheck} from "../../../libraries/util/SafetyCheck";
import {GridRowSelectionModel} from "@mui/x-data-grid/models/gridRowSelectionModel";
import {Configuration} from "../../../global/Configuration";
import {UploadUrlResult} from "../models/UploadUrlResult";
import {toast} from "react-toastify";
import {dataProviders} from "../Data";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const ProductsOverview = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Typography color="text.primary">{translate("Products")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<ProductsOverviewContent/>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

export function ProductsOverviewContent(props: { tenant?: string }) {
	const supplierId = useParams()?.supplierId;
	const supplierKrn = supplierId ? `:product-supplier:${supplierId}:` : undefined;
	const [rowSelectionModel, setRowSelectionModel] =
		React.useState<GridRowSelectionModel>([]);
	
	const [selectedPartnerHierarchy, setSelectedPartnerHierarchy] = useState<PartnerHierarchy | null>(null);
	const [gridKrns, setGridKrns] = useState<string[]>([]);
	
	function setTheData(items: any[]) {
		if (JSON.stringify(gridKrns) !== JSON.stringify(items.map(x => x.krn))) {
			setGridKrns(items.map(x => x.krn));
		}
		
	}
	
	function onSelectionModelChange(selectionModel: GridRowSelectionModel) {
		setRowSelectionModel(selectionModel);
	}
	
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Link"),
			renderCell: (params) => (
				<Link to={linkProductDetailsFor(params.row.krn)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
		},
		{
			field: "supplierName",
			headerName: translate("SupplierName"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "krn",
			headerName: translate("Kizi Resource Name"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "name",
			headerName: translate("Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "clientName",
			headerName: translate("Client Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "externalId",
			headerName: translate("External ID"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "dataProviderKrn",
			headerName: translate("Data Provider KRN"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "description",
			headerName: translate("Description"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productSupplierKrn",
			headerName: translate("Product Supplier KRN"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productType",
			headerName: translate("Product Type"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productTypeKrn",
			headerName: translate("Product Type KRN"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "orderType",
			headerName: translate("Order Type"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: tooltipColumn(productOrderTypeColumn()),
			valueOptions: ["AFFILIATE", "FUNNEL_MGA", "NOT_ORDERABLE"],
			valueFormatter: (params) => formatProductOrderType(params.value),
			flex: 1,
		},
		{
			field: "productCategory",
			headerName: translate("Product Category"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: productCategoryColumn(),
			valueOptions: [
				"NON_SPECIFIC",
				"CAR",
				"BUSINESS_CAR",
				"ENERGY",
				"HOME",
				"LIABILITY",
				"TRIPLE_PLAY",
				"TRAVEL",
				"HEALTH_CARE",
				"FUNERAL",
				"LOAN",
				"SIM_ONLY",
				"ANIMAL",
				"BIKE",
				"SOLAR_PANELS",
				"SAVINGS",
				"EVENT_BASED",
				"BUSINESS_LIABILITY_INSURANCE",
				"BUSINESS_VAN",
				"BUSINESS_DAMAGE",
				"BUSINESS_ADDITIONAL_COSTS",
				"BUSINESS_LEGAL",
				"BUSINESS_COMPUTER_ELECTRONICS",
				"BUSINESS_INVENTORY",
				"BUSINESS_BUILDING",
				"BUSINESS_ACCIDENT"
			],
			valueFormatter: (params) => formatProductCategory(params.value),
			flex: 1,
		},
		{
			field: "affiliateUrl",
			headerName: translate("Affiliate URL"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "insuranceCardUrl",
			headerName: translate("Insurance Card URL"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "termsAndConditionsUrl",
			headerName: translate("Terms & Conditions URL"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "supplierGenericProduct",
			headerName: translate("Supplier Generic Product"),
			type: "boolean",
			minWidth: 150,
			// renderCell: textColumn(),
			flex: 1,
		},
		{
			field: "state",
			headerName: translate("State"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: tooltipColumn(productStatusColumn()),
			valueOptions: ["ACTIVE", "DELETED"],
			valueFormatter: (params) => formatProductStatus(params.value),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			type: "dateTime",
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			type: "dateTime",
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		}
	];
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	
	return <GraphQlDataGrid
		key={`products-grid-${props.tenant ?? "none"}`}
		rows={[]}
		onDataFetched={setTheData}
		onDataRefresh={() => setTheData([])}
		checkboxSelection={!!selectedPartnerHierarchy}
		onRowSelectionModelChange={onSelectionModelChange}
		query={PRODUCTS_OVERVIEW}
		graphFunction={"productsExtended"}
		client={graphQLServices.productsClient}
		rowsPerPageOptions={[50, 500, 1000]}
		initialPageSize={50}
		columns={columns}
		getRowId={row => row.krn}
		defaultSortModel={[
			{
				field: "name",
				sort: "asc"
			}
		]}
		// default filter as described in SUR-136
		defaultFilterModel={{
			items: [
				{
					field: "state",
					value: "ACTIVE",
					operator: "is",
				},
				(supplierKrn && {
					field: "krn",
					value: supplierKrn,
					operator: "contains",
				}) as GridFilterItem,
				(props.tenant && {
					field: "krn",
					value: `krn:${props.tenant}:`,
					operator: "contains",
				}) as GridFilterItem,
			].filter(it => !!it)
		}}
		queryVariables={{stage}}
		initialState={{
			columns: {
				columnVisibilityModel: {
					dataProviderKrn: false,
					description: false,
					productSupplierKrn: false,
					productType: false,
					productTypeKrn: false,
					affiliateUrl: false,
					insuranceCardUrl: false,
					termsAndConditionsUrl: false,
					supplierGenericProduct: false,
					creationDateTime: false,
				}
			}
		}}
		slots={{
			footer: CustomFooterFactory(stage, gridKrns, rowSelectionModel, selectedPartnerHierarchy, (selectedPartnerHierarchy: PartnerHierarchy | null) => setSelectedPartnerHierarchy(selectedPartnerHierarchy), supplierId)
		}}
	/>;
}

const productTypes = [
	"krn:kizi:product-type:insurance/damage/household",
	"krn:kizi:product-type:insurance/damage/liability",
	"krn:kizi:product-type:insurance/building-household-package",
	"krn:kizi:product-type:insurance/damage/building",
	"krn:kizi:product-type:insurance/damage/travel",
	"krn:kizi:product-type:insurance/healthcare",
	"krn:kizi:product-type:contract/triple-play",
	"krn:kizi:product-type:contract/solar-panels",
	"krn:kizi:product-type:contract/energy",
	"krn:kizi:product-type:banking/savings",
	"krn:kizi:product-type:insurance/event-based",
	"krn:kizi:product-type:insurance/car",
	"krn:kizi:product-type:insurance/business-car",
	"krn:kizi:product-type:business/car",
	"krn:kizi:product-type:business/van",
	"krn:kizi:product-type:business/businessdamage",
	"krn:kizi:product-type:business/additionalcosts",
	"krn:kizi:product-type:business/legal",
	"krn:kizi:product-type:business/computer_electronics",
	"krn:kizi:product-type:business/liability",
	"krn:kizi:product-type:business/inventory",
	"krn:kizi:product-type:business/building",
	"krn:kizi:product-type:business/accident"
];

function CustomFooterFactory(stage: string, gridKrns: string[], selectedItems: GridRowId[], selectedPartnerHierarchy: PartnerHierarchy | null, setSelectedPartnerHierarchy: (selectedPartnerHierarchy: PartnerHierarchy | null) => void, supplierId?: string) {
	return function CustomFooter(props: any) {
		const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>();
		const [openDownload, setOpenDownload] = useState(false);
		const [openUpload, setOpenUpload] = useState(false);
		const [selectedFile, setSelectedFile] = useState(null);
		const [open, setOpen] = useState(false);
		const [openCopy, setOpenCopy] = useState(false);
		const [copyConfirmation, setCopyConfirmation] = useState(false);
		const [copyCount, setCopyCount] = useState(0);
		const [productSupplierId, setProductSupplierId] = useState("");
		const [productSupplierKrn, setProductSupplierKrn] = useState("");
		const [clientName, setClientName] = useState("");
		const [externalId, setExternalId] = useState("");
		const [name, setName] = useState("");
		const [description, setDescription] = useState("");
		const [dataProvider, setDataProvider] = useState("");
		const [productType, setProductType] = useState("");
		const [selectedSupplier, setSelectedSupplier] = useState<{ label: string; id: string; value: string } | null>(null);
		const [exporterOptions, setExporterOptions] = useState<string[]>();
		const [selectedExportOption, setSelectedExportOption] = useState<string>();
		const handleClickOpen = () => {
			setOpen(true);
		};
		
		const handleClose = () => {
			setOpen(false);
			setProductSupplierKrn("");
			setProductSupplierId("");
			setSelectedSupplier(null);
			setClientName("");
			setExternalId("");
			setName("");
			setDescription("");
			setDataProvider("");
			setProductType("");
		};
		
		const handleCopyOpen = () => {
			setOpenCopy(true);
		};
		
		const handleCopyClose = () => {
			setOpenCopy(false);
			setCopyCount(0);
			setCopyConfirmation(false);
		};
		
		const handleDownloadOpen = () => {
			setOpenDownload(true);
		};
		
		const handleDownloadClose = () => {
			setOpenDownload(false);
		};
		
		const handleUploadClose = () => {
			setOpenUpload(false);
		};
		
		const handleUploadOpen = () => {
			setOpenUpload(true);
		};
		
		const exporterOptionsQuery = useQuery(GET_EXPORTER_OPTIONS, {
			client: graphQLServices.productsClient,
			fetchPolicy: "no-cache",
		});
		
		if (!exporterOptionsQuery.loading && !exporterOptionsQuery.error && !exporterOptions) {
			const exporterItems = exporterOptionsQuery.data.exporterOptions.items.map((it: any) => (it.exporterName));
			setExporterOptions(exporterItems);
		}
		
		
		const supplierIndex = useQuery(SUPPLIER_INDEX, {
			client: graphQLServices.productsClient,
			fetchPolicy: "no-cache",
			variables: {
				stage: productsEditorTargetStage,
			}
		});
		
		let productSuppliers: Array<{ label: string; id: string; value: string }> = [];
		if (supplierIndex.loading || supplierIndex.error)
			productSuppliers = [];
		else
			productSuppliers = supplierIndex.data.productSuppliers.nodes
				.map((it: any) => ({label: it.legalName, id: it.id, value: it.krn}));
		
		
		const partnerHierarchyIndex = useQuery(PARTNER_HIERARCHY_INDEX, {
			client: graphQLServices.productsClient,
			fetchPolicy: "no-cache",
			variables: {
				stage: productsEditorTargetStage,
			}
		});
		
		let partnerHierarchy: PartnerHierarchy [] = [];
		if (partnerHierarchyIndex.loading || partnerHierarchyIndex.error)
			partnerHierarchy = [];
		else
			partnerHierarchy = partnerHierarchyIndex.data.partnerHierarchy.nodes;
		
		// Pre-select supplier when modal opens if on supplier details page
		useEffect(() => {
			if (open && supplierId && productSuppliers.length > 0 && !selectedSupplier) {
				const matchingSupplier = productSuppliers.find(supplier => {
					const supplierIdFromKrn = supplier.value.split(":").pop();
					return supplierIdFromKrn === supplierId;
				});
				if (matchingSupplier) {
					setSelectedSupplier(matchingSupplier);
					setProductSupplierKrn(matchingSupplier.value);
					setProductSupplierId(matchingSupplier.id);
				}
			}
		}, [open, supplierId, productSuppliers, selectedSupplier]);
		
		const handleCreate = () => {
			const supplierId = productSupplierKrn.split(":").last();
			const providerId = dataProvider.split(":").last();
			const productId = providerId === "risk" && externalId.length > 9 ? externalId.substring(9, externalId.length) : externalId;
			const krn = `krn:${clientName}:product-supplier:${supplierId}:product:${providerId}/${productId}`;
			setOpen(false);
			openProductDetailsForNew(krn, clientName, productSupplierId, productSupplierKrn, externalId, name, description, dataProvider, productType);
		};
		
		async function handleCopy() {
			const items = selectedItems.map((x) => x.toString());
			await saveCopy(items, selectedPartnerHierarchy?.clientName ?? "");
		}
		
		async function saveCopy(krns: string[], toClient: string) {
			targetStageSafetyCheck(stage);
			
			const result = await graphQLServices.productsClient.mutate({
				mutation: COPY_PRODUCTS,
				variables: {
					krns,
					toClient,
					stage
				}
			});
			setCopyCount(result.data.copyProducts.copyCount);
			setCopyConfirmation(true);
		}
		
		async function downloadTemplate() {
			const krns = gridKrns;
			const response = await graphQLServices.productsClient.query({
				query: GET_EXPORT_TEMPLATE,
				variables: {
					item: {
						krns,
						exporterName: selectedExportOption
					},
					stage: "kizidev"
				}
			});
			const url = response.data.exportTemplate?.url;
			if (!url)
				throw new Error("Could not generate preassinged url");
			
			// Create the temporary anchor element
			const anchor = document.createElement("a");
			anchor.href = url;
			anchor.target = "_blank"; // Open in a new tab
			anchor.rel = "noopener noreferrer"; // Security settings
			document.body.appendChild(anchor); // Append to body to make it clickable
			
			// Trigger the download
			anchor.click();
			
			// Clean up by removing the anchor from the DOM
			document.body.removeChild(anchor);
		}
		
		async function uploadFileClick() {
			if (selectedFile) {
				const response = await graphQLServices.productsClient.query({
					query: GET_UPLOAD_URL,
					variables: {
						stage: "kizidev"
					}
				});
				
				const uploadResponse: UploadUrlResult = response.data.uploadUrl;
				if (!uploadResponse)
					throw new Error("Could find upload url Response");
				
				await uploadXlsxToS3(selectedFile, uploadResponse.url, uploadResponse.userName);
				
				setSelectedFile(null);
			}
			//window.open(url, "_blank");
		}
		
		async function uploadXlsxToS3(fileToUpload: File, uploadUrl: string, userName: string) {
			const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
			const requestInit = {
				method: "PUT",
				body: fileToUpload,
				headers: new Headers({
					"Content-Type": contentType,
					"X-Amz-Meta-User": userName
				})
			};
			try {
				const response = await fetch(new Request(uploadUrl, requestInit));
				if (response && response.ok)
					toast.success(`Import requested, you will receive an email.`);
				return response && response.ok;
			} catch (ex) {
				setUploadErrorMessage("Upload failed");
			}
		}
		
		const handleFileChange = (event: any) => {
			const file = event.target.files[0];
			if (file && file.name.endsWith(".xlsx")) {
				setSelectedFile(file);
			} else {
				setSelectedFile(null);
				setUploadErrorMessage("Please select an .xlsx file."); // Optional: show an alert or handle this validation as needed
			}
		};
		
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					key={"handleClickOpen"}
					variant={"contained"}
					onClick={handleClickOpen}
					disabled={stage !== productsEditorTargetStage}
				>{translate("Add")}</Button>
				{partnerHierarchy.length > 0 &&
					<Button
						style={{width: "150px"}}
						key={"toggleOpenCopy"}
						variant={"contained"}
						onClick={() => {
							if (selectedPartnerHierarchy) {
								setSelectedPartnerHierarchy(null);
							} else {
								setSelectedPartnerHierarchy(partnerHierarchy[0]);
							}
						}}
						disabled={stage !== productsEditorTargetStage}
					>{translate(selectedPartnerHierarchy ? "Discard copy" : "Copy")}</Button>
				}
				{selectedPartnerHierarchy &&
					<span>{translate("Copy to partner:")}</span>
				}
				{selectedPartnerHierarchy && partnerHierarchy.length > 0 &&
					<FormControl key={"partnerHierarchyClientNameFromControl"} margin={"dense"}>
						<Select
							style={{width: "200px"}}
							label={translate("Copy to")}
							variant={"standard"}
							required
							value={selectedPartnerHierarchy.id}
							onChange={event => {
								setSelectedPartnerHierarchy(partnerHierarchy.find(x => x.id === event.target.value) ?? null);
							}}
						>
							{partnerHierarchy.map(it =>
								<MenuItem key={it.id} value={it.id}>{it.clientName}</MenuItem>)}
						</Select>
					</FormControl>}
				{selectedItems.length > 0 && selectedPartnerHierarchy &&
					<Button
						key={"copyProducts"}
						variant={"contained"}
						onClick={handleCopyOpen}
						disabled={stage !== productsEditorTargetStage}
					>{translate("Copy Products")}</Button>}
				
				<Button
					key={"downloadsProducts"}
					variant={"contained"}
					onClick={handleDownloadOpen}
					disabled={stage !== productsEditorTargetStage}
				>{translate("See Import templates")}</Button>
				
				<Button
					key={"Import Excel File"}
					variant={"contained"}
					onClick={handleUploadOpen}
					disabled={stage !== productsEditorTargetStage}
				>{translate("Import Excel File")}</Button>
			</Stack>
			<Dialog open={openCopy} onClose={handleCopyClose}>
				<DialogTitle>{translate("Copy Products")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{!copyConfirmation && translate("Copy {numberOfItems} items to {clientName}?").replace("{numberOfItems}", selectedItems.length.toString())
							.replace("{clientName}", selectedPartnerHierarchy?.clientName ?? "")}
						
						{copyConfirmation && copyCount > 0 && translate("Successfully copied {copyCount} items to {clientName}.").replace("{copyCount}", copyCount.toString())
							.replace("{clientName}", selectedPartnerHierarchy?.clientName ?? "")}
						
						{copyConfirmation && copyCount === 0 && translate("No items copied to {clientName}.")
							.replace("{clientName}", selectedPartnerHierarchy?.clientName ?? "")}
					</DialogContentText>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						{copyConfirmation && <Button onClick={handleCopyClose}>{translate("Close")}</Button>}
						{!copyConfirmation && <Button onClick={handleCopyClose}>{translate("Cancel")}</Button>}
						{!copyConfirmation &&
							<Button onClick={() => handleCopy()} color={"success"}>{translate("Copy")}</Button>}
					</Stack>
				</DialogContent>
			</Dialog>
			<Dialog open={openDownload} onClose={handleDownloadClose}>
				<DialogTitle>{translate("Imports Templates")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{exporterOptions && exporterOptions.length > 0 &&
							<FormControl key={"exporterOptionsFromControl"} margin={"dense"}>
								<Select
									style={{width: "200px"}}
									label={translate("Template")}
									variant={"standard"}
									required
									value={selectedExportOption}
									onChange={event => {
										setSelectedExportOption(event.target.value ?? null);
									}}
								>
									{exporterOptions.map(it =>
										<MenuItem key={it} value={it}>{it}</MenuItem>)}
								</Select>
							</FormControl>}
					
					</DialogContentText>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						<Button onClick={handleDownloadClose}>{translate("Close")}</Button>
						<Button onClick={downloadTemplate}>{translate("Download")}</Button>
					</Stack>
				</DialogContent>
			</Dialog>
			<Dialog open={openUpload} onClose={handleUploadClose}>
				<DialogTitle>{translate("Imports Templates")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Select a file to upload:")}
					</DialogContentText>
					<input
						type="file"
						onChange={handleFileChange}
						style={{marginTop: "10px", marginBottom: "20px"}}
					/>
					{uploadErrorMessage &&
						<Alert
							severity={"error"}>{uploadErrorMessage}</Alert>
					}
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						<Button onClick={handleUploadClose}>{translate("Close")}</Button>
						<Button disabled={!selectedFile} onClick={uploadFileClick}>{translate("Upload")}</Button>
					</Stack>
				</DialogContent>
			</Dialog>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New Product")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<FormControl
						margin={"dense"}
						fullWidth>
						<InputLabel id={"clientName"}>{translate("Client Name")}</InputLabel>
						<Select
							labelId={"clientName"}
							label={translate("Client Name")}
							variant={"standard"}
							value={clientName}
							onChange={event => {
								setClientName(event.target.value);
							}}
							required
						>
							<MenuItem key={"undefined"} value={undefined} hidden={true}
								style={{display: "none"}}></MenuItem>
							<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
							{Configuration.clientNames.map(it =>
								<MenuItem key={it} value={it}>{translate(it)}</MenuItem>)}
						</Select>
					</FormControl>
					<Autocomplete
						disablePortal
						options={productSuppliers}
						value={selectedSupplier}
						autoSelect
						autoComplete
						renderInput={(params) => <TextField
							{...params}
							label={translate("Product Supplier")}
							variant={"standard"}
						/>}
						getOptionLabel={(it: any) => it.label}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						onChange={(event, item) => {
							setSelectedSupplier(item);
							if (item) {
								setProductSupplierKrn(item.value);
								setProductSupplierId(item.id);
							}
						}}
					/>
					<TextField
						margin="dense"
						label={translate("External ID")}
						fullWidth
						variant="standard"
						required
						value={externalId}
						onChange={event => {
							setExternalId(event.target.value);
						}}
					/>
					<TextField
						margin="dense"
						label={translate("Name")}
						fullWidth
						variant="standard"
						required
						value={name}
						onChange={event => {
							setName(event.target.value);
						}}
					/>
					<TextField
						margin="dense"
						label={translate("Description")}
						fullWidth
						variant="standard"
						required
						value={description}
						onChange={event => {
							setDescription(event.target.value);
						}}
					/>
					<FormControl
						margin={"dense"}
						fullWidth>
						<InputLabel id={"dataProvider"}>{translate("Data Provider")}</InputLabel>
						<Select
							labelId={"dataProvider"}
							label={translate("Data Provider")}
							variant={"standard"}
							required
							value={dataProvider}
							onChange={event => {
								setDataProvider(event.target.value);
							}}
						>
							<MenuItem key={"undefined"} value={undefined} hidden={true}
								style={{display: "none"}}></MenuItem>
							<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
							{dataProviders.map(it =>
								<MenuItem key={it} value={it}>{translate(it)}</MenuItem>)}
						</Select>
					</FormControl>
					<FormControl
						margin={"dense"}
						fullWidth>
						<InputLabel id={"productType"}>{translate("Product Type")}</InputLabel>
						<Select
							labelId={"productType"}
							label={translate("Product Type")}
							variant={"standard"}
							required
							value={productType}
							onChange={event => {
								setProductType(event.target.value);
							}}
						>
							<MenuItem key={"undefined"} value={undefined} hidden={true}
								style={{display: "none"}}></MenuItem>
							<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
							{productTypes.map(it =>
								<MenuItem key={it} value={it}>{translate(it)}</MenuItem>)}
						</Select>
					</FormControl>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						<Button onClick={handleClose}>{translate("Cancel")}</Button>
						<Button onClick={handleCreate} color={"success"}>{translate("Create")}</Button>
					</Stack>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} labelRowsPerPage={translate("Rows per page:")}/>
		</Stack>;
	};
}

function formatProductStatus(text: string | null): string {
	return formatEnum("productStatus", text);
}

function formatProductOrderType(text: string | null): string {
	return formatEnum("productOrderType", text);
}

function formatProductCategory(text: string | null): string {
	return formatEnum("productCategory", text);
}

function formatEnum(prefix: string, text: string | null): string {
	if (!text)
		return "";
	return translate(text)
		?? translate("Unknown");
}
